import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {
  Users,
  MessageSquare,
  Calendar,
  FileText,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Clock,
  BarChart3,
  GripVertical,
  FolderOpen,
  ArrowRight,
  Settings,
  Eye,
  EyeOff,
  X,
  Sparkles,
  TrendingUp,
  Wallet,
  LineChart,
  Timer,
  Inbox,
  CalendarDays,
  AlertTriangle,
  Phone,
  PhoneIncoming
} from '../icons/wp-icons';
import apiClient from '../api/apiClient';
import { dashboardApi } from '../api/dashboardApi';
import Layout from '../components/Layout/Layout';
import UmsatzChart from '../components/Dashboard/UmsatzChart';
import TopKunden from '../components/Dashboard/TopKunden';
import StatistikCard from '../components/Dashboard/StatistikCard';
import ZeiterfassungWidget from '../components/Dashboard/ZeiterfassungWidget';
import ProaktiveAktionenWidget from '../components/Dashboard/ProaktiveAktionenWidget';
import AnrufePoolWidget from '../components/Dashboard/AnrufePoolWidget';
import AnfrageHoverPreview from '../components/AnfrageHoverPreview';
import DashboardKpiBar from '../components/Dashboard/DashboardKpiBar';
import MobileDashboard from '../components/Mobile/MobileDashboard';
import { useDeviceType } from '../hooks/useIsMobile';
import { useGlobalDataRefresh } from '../hooks/useDataRefresh';
import { usePermissions } from '../context/PermissionContext';
import { useAuth } from '../context/AuthContext';

const ResponsiveGridLayout = WidthProvider(Responsive);

// Standard-Layout für verschiedene Breakpoints - optimiert für bessere Raumnutzung
const defaultLayouts = {
  lg: [
    // Anrufe-Pool prominent oben für HeyKiki-Orgs (Lucas/Husmann&Dreier-
    // Feedback 2026-05-07). Bei Standalone-Orgs filtert isWidgetVisible
    // das Widget raus, sodass es im Layout nicht angezeigt wird.
    { i: 'anrufe-pool', x: 0, y: 0, w: 12, h: 10, minH: 8 },
    { i: 'ki-vorschlaege', x: 0, y: 10, w: 4, h: 12, minH: 10 },
    { i: 'stats-basis', x: 4, y: 10, w: 8, h: 4, minH: 3 },
    { i: 'stats-monat', x: 4, y: 14, w: 8, h: 4, minH: 3 },
    { i: 'forderungen', x: 4, y: 18, w: 8, h: 4, minH: 3 },
    { i: 'umsatz-chart', x: 0, y: 22, w: 12, h: 10, minH: 9 },
    { i: 'zeiterfassung', x: 0, y: 32, w: 12, h: 14, minH: 12 },
    { i: 'projekte', x: 0, y: 46, w: 6, h: 8, minH: 6 },
    { i: 'top-kunden', x: 6, y: 46, w: 6, h: 8, minH: 6 },
    { i: 'offene-anfragen', x: 0, y: 54, w: 6, h: 6, minH: 5 },
    { i: 'anstehende-termine', x: 6, y: 54, w: 6, h: 6, minH: 5 },
  ],
  md: [
    { i: 'anrufe-pool', x: 0, y: 0, w: 10, h: 10, minH: 8 },
    { i: 'ki-vorschlaege', x: 0, y: 10, w: 10, h: 10, minH: 8 },
    { i: 'stats-basis', x: 0, y: 20, w: 10, h: 4, minH: 3 },
    { i: 'stats-monat', x: 0, y: 24, w: 10, h: 4, minH: 3 },
    { i: 'forderungen', x: 0, y: 28, w: 10, h: 4, minH: 3 },
    { i: 'umsatz-chart', x: 0, y: 32, w: 10, h: 10, minH: 9 },
    { i: 'zeiterfassung', x: 0, y: 42, w: 10, h: 14, minH: 12 },
    { i: 'projekte', x: 0, y: 56, w: 5, h: 8, minH: 6 },
    { i: 'top-kunden', x: 5, y: 56, w: 5, h: 8, minH: 6 },
    { i: 'offene-anfragen', x: 0, y: 64, w: 5, h: 6, minH: 5 },
    { i: 'anstehende-termine', x: 5, y: 64, w: 5, h: 6, minH: 5 },
  ],
  sm: [
    { i: 'anrufe-pool', x: 0, y: 0, w: 6, h: 10, minH: 8 },
    { i: 'ki-vorschlaege', x: 0, y: 10, w: 6, h: 10, minH: 8 },
    { i: 'stats-basis', x: 0, y: 20, w: 6, h: 6, minH: 5 },
    { i: 'stats-monat', x: 0, y: 26, w: 6, h: 6, minH: 5 },
    { i: 'forderungen', x: 0, y: 32, w: 6, h: 5, minH: 4 },
    { i: 'umsatz-chart', x: 0, y: 37, w: 6, h: 10, minH: 9 },
    { i: 'zeiterfassung', x: 0, y: 47, w: 6, h: 16, minH: 14 },
    { i: 'projekte', x: 0, y: 63, w: 6, h: 8, minH: 6 },
    { i: 'top-kunden', x: 0, y: 71, w: 6, h: 8, minH: 6 },
    { i: 'offene-anfragen', x: 0, y: 79, w: 6, h: 6, minH: 5 },
    { i: 'anstehende-termine', x: 0, y: 85, w: 6, h: 6, minH: 5 },
  ],
};

const PROJEKT_STATUS_CONFIG = {
  PLANUNG: { label: 'Planung', color: 'bg-gray-100 text-gray-700' },
  IN_BEARBEITUNG: { label: 'In Bearbeitung', color: 'bg-blue-100 text-blue-700' },
  ABGESCHLOSSEN: { label: 'Abgeschlossen', color: 'bg-green-100 text-green-700' },
  ABGERECHNET: { label: 'Abgerechnet', color: 'bg-purple-100 text-purple-700' }
};

// v11 (2026-05-07): Anrufe-pro-Mitarbeiter-Pool-Widget hinzugefügt — Bump
// damit Bestandsuser das neue Default-Layout bekommen statt das v10 ohne
// das Widget zu cachen.
const LAYOUT_STORAGE_KEY = 'dashboard-widget-layouts-v11';
const WIDGET_VISIBILITY_KEY = 'dashboard-widget-visibility-v1';

const VOICE_RANGE_OPTIONS = [
  { value: 'today', label: 'Heute' },
  { value: 'yesterday', label: 'Gestern' },
  { value: 'week', label: 'Diese Woche' },
  { value: 'month', label: 'Dieser Monat' },
  { value: 'quarter', label: 'Dieses Quartal' },
  { value: 'year', label: 'Dieses Jahr' },
  { value: 'custom', label: 'Benutzerdefiniert' },
];

// Liefert ein [start, end)-Fenster für die gewählte Range — end ist exklusiv.
// `isCurrentMonth` schaltet die Inklusivminuten-Quote auf der UI frei.
function resolveVoiceRange(range, customStart, customEnd) {
  const now = new Date();
  const start = new Date(now);
  const end = new Date(now);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  end.setDate(end.getDate() + 1); // default: heute
  switch (range) {
    case 'today':
      break;
    case 'yesterday':
      start.setDate(start.getDate() - 1);
      end.setDate(end.getDate() - 1);
      break;
    case 'week': {
      // Montag als Wochenstart (ISO)
      const dow = start.getDay() === 0 ? 6 : start.getDay() - 1;
      start.setDate(start.getDate() - dow);
      break;
    }
    case 'month':
      start.setDate(1);
      break;
    case 'quarter': {
      const q = Math.floor(start.getMonth() / 3);
      start.setMonth(q * 3, 1);
      break;
    }
    case 'year':
      start.setMonth(0, 1);
      break;
    case 'custom': {
      if (customStart) {
        const s = new Date(customStart); s.setHours(0, 0, 0, 0);
        start.setTime(s.getTime());
      }
      if (customEnd) {
        const e = new Date(customEnd); e.setHours(0, 0, 0, 0); e.setDate(e.getDate() + 1);
        end.setTime(e.getTime());
      }
      break;
    }
    default:
      break;
  }
  // isCurrentMonth: Range deckt exakt den laufenden Kalendermonat ab
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const isCurrentMonth =
    range === 'month' ||
    (start.getTime() === monthStart.getTime() && end.getTime() === monthEnd.getTime());
  return { start, end, isCurrentMonth };
}

function describeVoiceRange(range, start, end) {
  const fmt = (d) => d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const label = VOICE_RANGE_OPTIONS.find(o => o.value === range)?.label || '';
  if (range === 'today' || range === 'yesterday') return label;
  const endInclusive = new Date(end); endInclusive.setDate(endInclusive.getDate() - 1);
  return `${label} · ${fmt(start)} – ${fmt(endInclusive)}`;
}

// Widget-Konfiguration mit Icons und Labels
const WIDGET_CONFIG = {
  'ki-vorschlaege': { label: 'KI-Vorschläge', icon: Sparkles, defaultVisible: true },
  // HeyKiki-Only-Widget — Sichtbarkeit zusätzlich gegated in isWidgetVisible
  // auf isHeykikiOrg, defaultVisible bleibt auf true damit es bei HeyKiki-
  // Orgs ohne Setup auftaucht.
  'anrufe-pool': { label: 'Anrufe pro Mitarbeiter', icon: PhoneIncoming, defaultVisible: true, heykikiOnly: true },
  'stats-basis': { label: 'Übersicht', icon: TrendingUp, defaultVisible: true },
  'stats-monat': { label: 'Diesen Monat', icon: CalendarDays, defaultVisible: true },
  'forderungen': { label: 'Offene Forderungen', icon: Wallet, defaultVisible: true },
  'umsatz-chart': { label: 'Umsatz-Entwicklung', icon: LineChart, defaultVisible: true },
  'zeiterfassung': { label: 'Zeiterfassung', icon: Timer, defaultVisible: true },
  'projekte': { label: 'Aktive Projekte', icon: FolderOpen, defaultVisible: true },
  'top-kunden': { label: 'Top Kunden', icon: Users, defaultVisible: true },
  'offene-anfragen': { label: 'Offene Anfragen', icon: Inbox, defaultVisible: true },
  'anstehende-termine': { label: 'Anstehende Termine', icon: Calendar, defaultVisible: true },
};

function Dashboard() {
  const { isMobile, isTablet } = useDeviceType();
  const { canView } = usePermissions();
  const { user } = useAuth();
  const isHeykikiOrg = user?.organisation?.orgType === 'HEYKIKI';
  const showKiVorschlaege = canView('KI_VORSCHLAEGE');
  const [loading, setLoading] = useState(true);
  const [showWidgetSettings, setShowWidgetSettings] = useState(false);
  // HeyKiki-spezifische Voice-Stats (nur für HEYKIKI-Orgs geladen)
  const [voiceStats, setVoiceStats] = useState(null);
  // Rohdaten für den Zeitraum-Filter: settings (inklusivMinuten) + alle Calls im Lookback-Fenster.
  // Die sichtbaren `voiceStats` werden daraus clientseitig via `voiceRange` berechnet.
  const [voiceRaw, setVoiceRaw] = useState(null);
  const [voiceRange, setVoiceRange] = useState('month');
  const [voiceCustomStart, setVoiceCustomStart] = useState('');
  const [voiceCustomEnd, setVoiceCustomEnd] = useState('');

  // Widget-Sichtbarkeit State
  const [widgetVisibility, setWidgetVisibility] = useState(() => {
    const saved = localStorage.getItem(WIDGET_VISIBILITY_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
    // Default: alle Widgets sichtbar
    const defaults = {};
    Object.keys(WIDGET_CONFIG).forEach(key => {
      defaults[key] = WIDGET_CONFIG[key].defaultVisible;
    });
    return defaults;
  });
  const [stats, setStats] = useState({
    kunden: 0,
    anfragen: 0,
    termine: 0,
    neueAnfragen: 0
  });
  // Org-weite Tageszähler für die KPI-Bar (eigene Termine/Anfragen liegen
  // bereits in `meineAnfragen`/`meineTermineHeute`, hier brauchen wir die
  // gesamte Org-Sicht).
  const [termineHeuteCount, setTermineHeuteCount] = useState(0);
  const [kiAnrufeHeuteCount, setKiAnrufeHeuteCount] = useState(0);
  const [recentAnfragen, setRecentAnfragen] = useState([]);
  const [upcomingTermine, setUpcomingTermine] = useState([]);
  const [meineAnfragen, setMeineAnfragen] = useState([]);
  const [meineTermineHeute, setMeineTermineHeute] = useState([]);
  const [umsatzDaten, setUmsatzDaten] = useState(null);
  const [topKunden, setTopKunden] = useState([]);
  const [offeneForderungen, setOffeneForderungen] = useState(null);
  const [statistiken, setStatistiken] = useState(null);
  const [zeitraum, setZeitraum] = useState('letzten-12-monate');
  const [aktiveProjekte, setAktiveProjekte] = useState([]);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [geschaeftszeitenConfigured, setGeschaeftszeitenConfigured] = useState(true);

  // Grid Layout State
  const [layouts, setLayouts] = useState(() => {
    const saved = localStorage.getItem(LAYOUT_STORAGE_KEY);
    return saved ? JSON.parse(saved) : defaultLayouts;
  });

  // Widget-Sichtbarkeit speichern
  const toggleWidgetVisibility = useCallback((widgetId) => {
    setWidgetVisibility(prev => {
      const newVisibility = { ...prev, [widgetId]: !prev[widgetId] };
      localStorage.setItem(WIDGET_VISIBILITY_KEY, JSON.stringify(newVisibility));
      return newVisibility;
    });
  }, []);

  // Alle Widgets ein/ausblenden
  const toggleAllWidgets = useCallback((visible) => {
    setWidgetVisibility(prev => {
      const newVisibility = {};
      Object.keys(WIDGET_CONFIG).forEach(key => {
        newVisibility[key] = visible;
      });
      localStorage.setItem(WIDGET_VISIBILITY_KEY, JSON.stringify(newVisibility));
      return newVisibility;
    });
  }, []);

  // Prüfe ob Widget sichtbar sein soll
  const isWidgetVisible = useCallback((widgetId) => {
    // KI-Vorschläge hängt auch von Berechtigung ab
    if (widgetId === 'ki-vorschlaege' && !showKiVorschlaege) {
      return false;
    }
    // Anrufe-Pool nur für HeyKiki-Orgs (Lucas/Husmann&Dreier-Feedback 2026-05-07)
    if (widgetId === 'anrufe-pool' && !isHeykikiOrg) {
      return false;
    }
    return widgetVisibility[widgetId] !== false;
  }, [widgetVisibility, showKiVorschlaege, isHeykikiOrg]);

  // Layouts basierend auf sichtbaren Widgets filtern
  const filteredLayouts = {
    lg: layouts.lg?.filter(item => isWidgetVisible(item.i)) || [],
    md: layouts.md?.filter(item => isWidgetVisible(item.i)) || [],
    sm: layouts.sm?.filter(item => isWidgetVisible(item.i)) || [],
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    loadUmsatzDaten();
  }, [zeitraum, customStart, customEnd]);

  // Voice-Stats bei Range-Wechsel clientseitig neu berechnen (kein API-Call nötig:
  // alle Calls liegen bereits in `voiceRaw.calls`). `currentMonthUsedMinutes` bleibt
  // der Einzige Zähler, der aus settings kommt — er wird nur beim Range='month'
  // gegen das Inklusiv-Kontingent verglichen.
  useEffect(() => {
    if (!voiceRaw) return;
    const { start, end, isCurrentMonth } = resolveVoiceRange(voiceRange, voiceCustomStart, voiceCustomEnd);
    const inRange = (d) => {
      const t = new Date(d).getTime();
      return t >= start.getTime() && t < end.getTime();
    };
    const callsInRange = voiceRaw.calls.filter(c => c.startedAt && inRange(c.startedAt));
    const inbound = callsInRange.filter(c => c.direction === 'inbound').length;
    const outbound = callsInRange.filter(c => c.direction === 'outbound').length;
    const rangeMinutes = Math.ceil(
      callsInRange.reduce((sum, c) => sum + (Number(c.durationSeconds) || 0), 0) / 60
    );
    setVoiceStats({
      // Beim aktuellen Monat den autoritativen Zähler aus settings verwenden
      // (berücksichtigt Calls, die noch nicht in call-logs gelanded sind).
      usedMinutes: isCurrentMonth ? voiceRaw.currentMonthUsedMinutes : rangeMinutes,
      inklusivMinuten: voiceRaw.inklusivMinuten,
      showQuota: isCurrentMonth,
      inboundCalls: inbound,
      outboundCalls: outbound,
      rangeLabel: describeVoiceRange(voiceRange, start, end)
    });
  }, [voiceRaw, voiceRange, voiceCustomStart, voiceCustomEnd]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      const [kundenRes, anfrogenRes, termineRes, projekteRes, geschaeftszeitenRes] = await Promise.all([
        apiClient.get('/kunden'),
        apiClient.get('/anfragen'),
        apiClient.get('/termine'),
        apiClient.get('/projekte'),
        apiClient.get('/geschaeftszeiten').catch(() => ({ data: [] }))
      ]);

      const kunden = kundenRes.data;
      const anfragen = anfrogenRes.data;
      const termine = termineRes.data;
      const projekte = projekteRes.data;
      const geschaeftszeiten = geschaeftszeitenRes.data;

      // Check if business hours are configured
      const aktiveGeschaeftszeiten = Array.isArray(geschaeftszeiten)
        ? geschaeftszeiten.filter(g => g.aktiv)
        : [];
      setGeschaeftszeitenConfigured(aktiveGeschaeftszeiten.length > 0);

      // Aktive Projekte filtern (PLANUNG oder IN_BEARBEITUNG) und die neuesten 5 anzeigen
      const aktiveProjekteListe = projekte
        .filter(p => p.status === 'PLANUNG' || p.status === 'IN_BEARBEITUNG')
        .sort((a, b) => new Date(b.erstelltAm) - new Date(a.erstelltAm))
        .slice(0, 5);
      setAktiveProjekte(aktiveProjekteListe);

      const offeneAnfragen = anfragen.filter(a =>
        a.status === 'neu' || a.status === 'in_bearbeitung'
      );

      const heute = new Date();
      heute.setHours(0, 0, 0, 0);
      const morgen = new Date(heute); morgen.setDate(morgen.getDate() + 1);
      const zukunftigeTermine = termine
        .filter(t => new Date(t.datum) >= heute)
        .sort((a, b) => new Date(a.datum) - new Date(b.datum));
      // Termine NUR für heute (org-weit) — für die KPI-Bar.
      const termineHeute = termine.filter(t => {
        const d = new Date(t.datum);
        return d >= heute && d < morgen;
      });
      setTermineHeuteCount(termineHeute.length);

      setStats({
        kunden: kunden.length,
        anfragen: offeneAnfragen.length,
        termine: zukunftigeTermine.length,
        neueAnfragen: anfragen.filter(a => a.status === 'neu').length
      });

      setRecentAnfragen(offeneAnfragen.slice(0, 5));
      setUpcomingTermine(zukunftigeTermine.slice(0, 5));

      // „Meine Arbeit": nur eigene Zuweisungen
      if (user?.id) {
        setMeineAnfragen(offeneAnfragen.filter(a => a.bearbeiterId === user.id).slice(0, 5));
        const heuteMorgen = new Date(heute); heuteMorgen.setDate(heuteMorgen.getDate() + 1);
        setMeineTermineHeute(
          termine.filter(t => {
            if (t.bearbeiterId !== user.id) return false;
            const d = new Date(t.datum);
            return d >= heute && d < heuteMorgen;
          }).sort((a, b) => (a.uhrzeit || '').localeCompare(b.uhrzeit || ''))
        );
      }

      const [topKundenData, forderungenData, statData] = await Promise.all([
        dashboardApi.getTopKunden(),
        dashboardApi.getOffeneForderungen(),
        dashboardApi.getStatistiken()
      ]);

      setTopKunden(topKundenData);
      setOffeneForderungen(forderungenData);
      setStatistiken(statData);

      // Voice-Stats nur für HEYKIKI-Orgs (sonst Rauschen auf Standalone-Dashboards
      // und überflüssige API-Calls). Fehler werden geschluckt, damit das übrige
      // Dashboard nicht blockiert.
      if (isHeykikiOrg) {
        try {
          const [settingsRes, callLogsRes] = await Promise.all([
            apiClient.get('/voice-assistant/settings'),
            apiClient.get('/voice-assistant/call-logs?limit=2000')
          ]);
          const vs = settingsRes.data || {};
          const calls = Array.isArray(callLogsRes.data?.calls) ? callLogsRes.data.calls : [];
          setVoiceRaw({
            inklusivMinuten: vs.inklusivMinuten || 0,
            currentMonthUsedMinutes: vs.usedMinutes || 0,
            calls
          });
          // KI-Anrufe HEUTE für die KPI-Bar (org-weit).
          const heute0 = new Date(); heute0.setHours(0, 0, 0, 0);
          const morgen0 = new Date(heute0); morgen0.setDate(morgen0.getDate() + 1);
          const heuteCount = calls.filter(c => {
            const t = c.startedAt ? new Date(c.startedAt).getTime() : 0;
            return t >= heute0.getTime() && t < morgen0.getTime();
          }).length;
          setKiAnrufeHeuteCount(heuteCount);
        } catch (e) {
          console.warn('Voice-Stats konnten nicht geladen werden:', e?.message);
        }
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUmsatzDaten = async () => {
    try {
      const data = await dashboardApi.getUmsatzDaten(
        zeitraum,
        zeitraum === 'custom' ? customStart : null,
        zeitraum === 'custom' ? customEnd : null
      );
      setUmsatzDaten(data);
    } catch (error) {
      console.error('Fehler beim Laden der Umsatzdaten:', error);
    }
  };

  // Echtzeit-Updates: Reagiere auf KI-Assistent Aktionen
  const handleGlobalRefresh = useCallback(() => {
    loadDashboardData();
    loadUmsatzDaten();
  }, [zeitraum, customStart, customEnd]);

  useGlobalDataRefresh(handleGlobalRefresh);

  const onLayoutChange = useCallback((currentLayout, allLayouts) => {
    // Wichtig: Wir müssen die Layout-Infos für ausgeblendete Widgets beibehalten!
    // Ansonsten verlieren sie ihre Größe wenn sie wieder eingeblendet werden.
    setLayouts(prevLayouts => {
      const mergedLayouts = {};

      ['lg', 'md', 'sm'].forEach(breakpoint => {
        const newItems = allLayouts[breakpoint] || [];
        const prevItems = prevLayouts[breakpoint] || [];
        const defaultItems = defaultLayouts[breakpoint] || [];

        // Erstelle eine Map der neuen Layout-Items (sichtbare Widgets)
        const newItemsMap = new Map(newItems.map(item => [item.i, item]));

        // Behalte alle Items: aktualisiere sichtbare, behalte versteckte
        mergedLayouts[breakpoint] = defaultItems.map(defaultItem => {
          // Wenn das Item in den neuen Layouts ist (sichtbar), verwende die neue Position
          if (newItemsMap.has(defaultItem.i)) {
            return newItemsMap.get(defaultItem.i);
          }
          // Wenn das Item in den vorherigen Layouts war, behalte es
          const prevItem = prevItems.find(p => p.i === defaultItem.i);
          if (prevItem) {
            return prevItem;
          }
          // Fallback auf default
          return defaultItem;
        });
      });

      localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(mergedLayouts));
      return mergedLayouts;
    });
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const isToday = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isTomorrow = (dateString) => {
    const date = new Date(dateString);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return date.toDateString() === tomorrow.toDateString();
  };

  const formatTerminDate = (dateString) => {
    if (isToday(dateString)) return 'Heute';
    if (isTomorrow(dateString)) return 'Morgen';
    return new Date(dateString).toLocaleDateString('de-DE', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit'
    });
  };

  // Widget-Wrapper-Komponente mit Drag-Handle
  const WidgetWrapper = ({ children, title, className = '', noPadding = false, noScroll = false, headerAction = null }) => (
    <div className={`bg-white rounded-xl shadow-sm h-full flex flex-col ${noScroll ? 'overflow-hidden' : ''} ${className}`}>
      <div
        className="flex items-center justify-between px-4 py-2 border-b border-gray-100 bg-gray-50/50 flex-shrink-0"
      >
        <div className="widget-drag-handle flex-1 cursor-grab active:cursor-grabbing flex items-center">
          <span className="text-sm font-medium text-gray-600">{title}</span>
        </div>
        <div className="flex items-center gap-2">
          {headerAction && (
            <div onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
              {headerAction}
            </div>
          )}
          <GripVertical className="w-4 h-4 text-gray-400 widget-drag-handle cursor-grab" />
        </div>
      </div>
      <div className={`flex-1 ${noScroll ? 'overflow-hidden' : 'overflow-auto'} ${noPadding ? '' : 'p-4'}`}>
        {children}
      </div>
    </div>
  );

  // Mobile/Tablet Version anzeigen
  if (isMobile || isTablet) {
    return <MobileDashboard />;
  }

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-serif font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600 mt-1">Widgets per Drag & Drop verschieben</p>
          </div>
          <button
            onClick={() => setShowWidgetSettings(!showWidgetSettings)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              showWidgetSettings
                ? 'bg-primary text-white'
                : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Settings className="w-4 h-4" />
            <span className="text-sm font-medium">Widgets</span>
          </button>
        </div>

        {/* Warnung: Geschäftszeiten nicht konfiguriert */}
        {!geschaeftszeitenConfigured && (
          <div className="mb-6 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-amber-800">
                <span className="font-medium">Geschäftszeiten nicht konfiguriert</span> – Die KI-Agenten verwenden Standard-Öffnungszeiten (Mo–Fr 08:00–17:00). Bitte konfigurieren Sie Ihre Geschäftszeiten in den Einstellungen für eine korrekte Terminfindung.
              </p>
            </div>
            <Link
              to="/kalender/geschaeftszeiten"
              className="flex-shrink-0 text-sm font-medium text-amber-700 hover:text-amber-900 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-lg transition-colors"
            >
              Jetzt konfigurieren
            </Link>
          </div>
        )}

        {/* KPI-Bar — Lucas/H&D-Feedback 2026-05-08: Wichtigste Kennzahlen
            sollen wieder „auf einen Blick" sichtbar sein, ohne dass der
            Disponent zum Stats-Widget scrollen muss. Voll konfigurierbar
            pro User (LocalStorage), Default-Set unterscheidet Standalone
            vs. HeyKiki. Stats-Widgets bleiben darunter als Drilldown. */}
        <DashboardKpiBar
          userId={user?.id}
          isHeykikiOrg={isHeykikiOrg}
          canViewRechnungen={canView('RECHNUNGEN')}
          stats={stats}
          statistiken={statistiken}
          offeneForderungen={offeneForderungen}
          meineAnfragen={meineAnfragen}
          meineTermineHeute={meineTermineHeute}
          termineHeuteCount={termineHeuteCount}
          voiceRaw={voiceRaw}
          kiAnrufeHeuteCount={kiAnrufeHeuteCount}
        />

        {/* Widget-Einstellungen Leiste */}
        {showWidgetSettings && (
          <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-gray-600" />
                <span className="font-medium text-gray-900">Widget-Sichtbarkeit</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleAllWidgets(true)}
                  className="text-xs px-3 py-1.5 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                >
                  Alle einblenden
                </button>
                <button
                  onClick={() => toggleAllWidgets(false)}
                  className="text-xs px-3 py-1.5 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
                >
                  Alle ausblenden
                </button>
                <button
                  onClick={() => setShowWidgetSettings(false)}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors ml-2"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {Object.entries(WIDGET_CONFIG).filter(([widgetId, config]) => {
                // HeyKiki-Only-Widgets bei Standalone-Orgs gar nicht in Settings anzeigen
                if (config.heykikiOnly && !isHeykikiOrg) return false;
                return true;
              }).map(([widgetId, config]) => {
                const Icon = config.icon;
                const isVisible = isWidgetVisible(widgetId);
                const isDisabled = widgetId === 'ki-vorschlaege' && !showKiVorschlaege;

                return (
                  <button
                    key={widgetId}
                    onClick={() => !isDisabled && toggleWidgetVisibility(widgetId)}
                    disabled={isDisabled}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                      isDisabled
                        ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
                        : isVisible
                          ? 'bg-primary/10 border-primary text-primary hover:bg-primary/20'
                          : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm font-medium truncate">{config.label}</span>
                    {isVisible && !isDisabled ? (
                      <Eye className="w-3.5 h-3.5 ml-auto flex-shrink-0" />
                    ) : (
                      <EyeOff className="w-3.5 h-3.5 ml-auto flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
            {!showKiVorschlaege && (
              <p className="text-xs text-gray-500 mt-3">
                * KI-Vorschläge sind aufgrund von Berechtigungen nicht verfügbar
              </p>
            )}
          </div>
        )}

        {/* HeyKiki-Voice-Stats-Widgets (fest oben, nicht draggable).
            Bewusst außerhalb des ResponsiveGridLayouts, damit sie bei HeyKiki-
            Sub-Orgs immer zuerst sichtbar sind und nicht mit dem Drag-Layout
            der übrigen Widgets kollidieren. */}
        {isHeykikiOrg && voiceStats && (
          <div className="mb-6">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <select
                value={voiceRange}
                onChange={(e) => setVoiceRange(e.target.value)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#81c264]/40"
              >
                {VOICE_RANGE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {voiceRange === 'custom' && (
                <>
                  <input
                    type="date"
                    value={voiceCustomStart}
                    onChange={(e) => setVoiceCustomStart(e.target.value)}
                    className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#81c264]/40"
                  />
                  <span className="text-sm text-gray-500">bis</span>
                  <input
                    type="date"
                    value={voiceCustomEnd}
                    onChange={(e) => setVoiceCustomEnd(e.target.value)}
                    className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#81c264]/40"
                  />
                </>
              )}
              <span className="text-xs text-gray-500 ml-1">{voiceStats.rangeLabel}</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl shadow-sm flex flex-col">
                <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 bg-gray-50/50">
                  <span className="text-sm font-medium text-gray-600">Gesprächsminuten</span>
                  <span className="text-xs text-gray-500">{voiceStats.rangeLabel}</span>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-11 h-11 rounded-full bg-[#81c264]/15 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-[#81c264]" />
                    </div>
                    <div className="flex items-end gap-2">
                      <span className={`text-3xl font-bold ${voiceStats.showQuota && voiceStats.usedMinutes >= voiceStats.inklusivMinuten && voiceStats.inklusivMinuten > 0 ? 'text-orange-600' : 'text-gray-900'}`}>
                        {voiceStats.usedMinutes}
                      </span>
                      {voiceStats.showQuota && voiceStats.inklusivMinuten > 0 ? (
                        <span className="text-gray-500 mb-1">/ {voiceStats.inklusivMinuten} Min</span>
                      ) : (
                        <span className="text-gray-500 mb-1">Min</span>
                      )}
                    </div>
                  </div>
                  {voiceStats.showQuota && voiceStats.inklusivMinuten > 0 && (
                    <>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            voiceStats.usedMinutes > voiceStats.inklusivMinuten
                              ? 'bg-orange-500'
                              : voiceStats.usedMinutes >= voiceStats.inklusivMinuten * 0.8
                                ? 'bg-orange-500'
                                : 'bg-[#81c264]'
                          }`}
                          style={{ width: `${Math.min(100, (voiceStats.usedMinutes / voiceStats.inklusivMinuten) * 100)}%` }}
                        />
                      </div>
                      {voiceStats.usedMinutes > voiceStats.inklusivMinuten && (
                        <p className="text-xs text-orange-600 mt-2">
                          Inklusivminuten aufgebraucht — +{voiceStats.usedMinutes - voiceStats.inklusivMinuten} Zusatzminuten
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm flex flex-col">
                <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 bg-gray-50/50">
                  <span className="text-sm font-medium text-gray-600">Eingehende Anrufe</span>
                  <span className="text-xs text-gray-500">{voiceStats.rangeLabel}</span>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-[#81c264]/15 flex items-center justify-center">
                      <PhoneIncoming className="w-5 h-5 text-[#81c264]" />
                    </div>
                    <span className="text-3xl font-bold text-gray-900">{voiceStats.inboundCalls}</span>
                  </div>
                  {voiceStats.outboundCalls > 0 && (
                    <p className="text-xs text-gray-500 mt-3">
                      Zusätzlich {voiceStats.outboundCalls} ausgehende{voiceStats.outboundCalls === 1 ? 'r' : ''} Anruf{voiceStats.outboundCalls === 1 ? '' : 'e'} im Zeitraum
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* „Meine Arbeit" Banner — nur wenn dem User etwas zugewiesen ist */}
        {(meineAnfragen.length > 0 || meineTermineHeute.length > 0) && (
          <div className="mb-6 bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-xl p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-primary" />
              <h2 className="text-base sm:text-lg font-semibold text-text">Meine aktuellen Aufgaben</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-text-muted uppercase">Offene Anfragen ({meineAnfragen.length})</p>
                  {meineAnfragen.length > 0 && (
                    <Link to="/anfragen?bearbeiter=me" className="text-xs text-primary hover:underline font-medium">Alle</Link>
                  )}
                </div>
                {meineAnfragen.length === 0 ? (
                  <p className="text-xs text-text-muted">Keine offenen Anfragen zugewiesen</p>
                ) : (
                  <ul className="space-y-1">
                    {meineAnfragen.map(a => (
                      <li key={a.id}>
                        <Link
                          to={`/anfragen/${a.id}`}
                          className="flex items-center gap-2 text-sm p-2 rounded-lg hover:bg-white/70 transition"
                        >
                          <Inbox className="w-4 h-4 text-primary flex-shrink-0" />
                          <span className="flex-1 truncate">{a.betreff}</span>
                          {a.kunde && <span className="text-xs text-text-muted truncate max-w-[120px]">{a.kunde.name}</span>}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-text-muted uppercase">Meine Termine heute ({meineTermineHeute.length})</p>
                  {meineTermineHeute.length > 0 && (
                    <Link to="/kalender" className="text-xs text-primary hover:underline font-medium">Kalender</Link>
                  )}
                </div>
                {meineTermineHeute.length === 0 ? (
                  <p className="text-xs text-text-muted">Heute keine Termine zugewiesen</p>
                ) : (
                  <ul className="space-y-1">
                    {meineTermineHeute.map(t => (
                      <li key={t.id} className="flex items-center gap-2 text-sm p-2 rounded-lg hover:bg-white/70 transition">
                        <Clock className="w-4 h-4 text-primary flex-shrink-0" />
                        <span className="font-medium w-12">{t.uhrzeit}</span>
                        <span className="flex-1 truncate">{t.kunde?.name || t.titel || '—'}</span>
                        {t.ort && <span className="text-xs text-text-muted truncate max-w-[120px]">{t.ort}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Draggable Grid */}
        <ResponsiveGridLayout
          className="layout"
          layouts={filteredLayouts}
          breakpoints={{ lg: 1200, md: 996, sm: 768 }}
          cols={{ lg: 12, md: 10, sm: 6 }}
          rowHeight={50}
          onLayoutChange={onLayoutChange}
          draggableHandle=".widget-drag-handle"
          useCSSTransforms={true}
          compactType="vertical"
          preventCollision={false}
          isResizable={false}
          margin={[16, 16]}
        >
          {/* Anrufe-pro-Mitarbeiter-Widget (HeyKiki-only).
              Lucas/Husmann&Dreier-Feedback 2026-05-07: Disponenten/Chef
              wollen auf einen Blick sehen, wer welche offenen Anrufe hat. */}
          {isWidgetVisible('anrufe-pool') && (
            <div key="anrufe-pool">
              <WidgetWrapper title="Anrufe pro Mitarbeiter" noScroll>
                <AnrufePoolWidget />
              </WidgetWrapper>
            </div>
          )}

          {/* KI-Vorschläge Widget */}
          {isWidgetVisible('ki-vorschlaege') && (
            <div key="ki-vorschlaege">
              <WidgetWrapper title="KI-Vorschläge">
                <ProaktiveAktionenWidget />
              </WidgetWrapper>
            </div>
          )}

          {/* Basis-Statistiken */}
          {isWidgetVisible('stats-basis') && (
          <div key="stats-basis">
            <WidgetWrapper title="Übersicht" noScroll>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 h-full">
                <div className="flex flex-col justify-center">
                  <StatistikCard
                    titel="Kunden Gesamt"
                    wert={stats.kunden}
                    icon={Users}
                    farbe="blue"
                  />
                </div>
                <div className="flex flex-col justify-center">
                  <StatistikCard
                    titel="Offene Anfragen"
                    wert={stats.anfragen}
                    icon={MessageSquare}
                    farbe="yellow"
                  />
                </div>
                <div className="flex flex-col justify-center">
                  <StatistikCard
                    titel="Anstehende Termine"
                    wert={stats.termine}
                    icon={Calendar}
                    farbe="purple"
                  />
                </div>
                <div className="flex flex-col justify-center">
                  <StatistikCard
                    titel="Neue Anfragen"
                    wert={stats.neueAnfragen}
                    icon={Clock}
                    farbe="red"
                  />
                </div>
              </div>
            </WidgetWrapper>
          </div>
          )}

          {/* Monats-Statistiken */}
          {isWidgetVisible('stats-monat') && (
          <div key="stats-monat">
            <WidgetWrapper title="Diesen Monat" noScroll>
              {statistiken && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 h-full">
                  <div className="flex flex-col justify-center">
                    <StatistikCard
                      titel="Umsatz"
                      wert={statistiken.umsatzMonat.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      einheit="€"
                      icon={DollarSign}
                      farbe="teal"
                    />
                  </div>
                  <div className="flex flex-col justify-center">
                    <StatistikCard
                      titel="Rechnungen"
                      wert={statistiken.rechnungenMonat}
                      icon={FileText}
                      farbe="green"
                    />
                  </div>
                  <div className="flex flex-col justify-center">
                    <StatistikCard
                      titel="KVAs"
                      wert={statistiken.kvasMonat}
                      icon={BarChart3}
                      farbe="blue"
                    />
                  </div>
                  <div className="flex flex-col justify-center">
                    <StatistikCard
                      titel="Ø Zahlungsziel"
                      wert={statistiken.durchschnittZahlungsziel}
                      einheit="Tage"
                      icon={Clock}
                      farbe="purple"
                    />
                  </div>
                </div>
              )}
            </WidgetWrapper>
          </div>
          )}

          {/* Offene Forderungen */}
          {isWidgetVisible('forderungen') && (
          <div key="forderungen">
            <WidgetWrapper title="Offene Forderungen" noScroll>
              {offeneForderungen ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-full">
                  <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-xl">
                    <div>
                      <p className="text-sm text-gray-600">Gesamt Offen</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {offeneForderungen.gesamt.summe.toLocaleString('de-DE', { minimumFractionDigits: 0 })}€
                      </p>
                      <p className="text-xs text-gray-500">{offeneForderungen.gesamt.anzahl} Rechnungen</p>
                    </div>
                    <AlertCircle className="w-8 h-8 text-yellow-600" />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl">
                    <div>
                      <p className="text-sm text-gray-600">Versendet</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {offeneForderungen.versendet.summe.toLocaleString('de-DE', { minimumFractionDigits: 0 })}€
                      </p>
                      <p className="text-xs text-gray-500">{offeneForderungen.versendet.anzahl} Rechnungen</p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-red-50 rounded-xl">
                    <div>
                      <p className="text-sm text-gray-600">Überfällig</p>
                      <p className="text-2xl font-bold text-red-600">
                        {offeneForderungen.ueberfaellig.summe.toLocaleString('de-DE', { minimumFractionDigits: 0 })}€
                      </p>
                      <p className="text-xs text-gray-500">{offeneForderungen.ueberfaellig.anzahl} Rechnungen</p>
                    </div>
                    <AlertCircle className="w-8 h-8 text-red-600" />
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  Laden...
                </div>
              )}
            </WidgetWrapper>
          </div>
          )}

          {/* Umsatz-Chart */}
          {isWidgetVisible('umsatz-chart') && (
          <div key="umsatz-chart">
            <WidgetWrapper title="Umsatz-Entwicklung" noScroll>
              <div className="h-full flex flex-col">
                <div className="flex flex-wrap gap-3 items-center mb-4 flex-shrink-0">
                  <select
                    value={zeitraum}
                    onChange={(e) => setZeitraum(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  >
                    <option value="dieser-monat">Dieser Monat</option>
                    <option value="letzter-monat">Letzter Monat</option>
                    <option value="letzten-3-monate">Letzte 3 Monate</option>
                    <option value="letzten-6-monate">Letzte 6 Monate</option>
                    <option value="letzten-12-monate">Letzte 12 Monate</option>
                    <option value="dieses-jahr">Dieses Jahr</option>
                    <option value="letztes-jahr">Letztes Jahr</option>
                    <option value="custom">Custom</option>
                  </select>

                  {zeitraum === 'custom' && (
                    <>
                      <input
                        type="date"
                        value={customStart}
                        onChange={(e) => setCustomStart(e.target.value)}
                        className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
                      />
                      <span className="text-gray-500 text-sm">bis</span>
                      <input
                        type="date"
                        value={customEnd}
                        onChange={(e) => setCustomEnd(e.target.value)}
                        className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
                      />
                    </>
                  )}

                  {umsatzDaten && (
                    <span className="text-xs text-gray-500 ml-auto">
                      {umsatzDaten.zeitraum.start} - {umsatzDaten.zeitraum.end}
                    </span>
                  )}
                </div>

                <div className="flex-1 min-h-0">
                  {umsatzDaten && <UmsatzChart daten={umsatzDaten} />}
                </div>

                {umsatzDaten && (
                  <div className="pt-4 border-t border-gray-100 flex-shrink-0">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1.5 mb-1">
                          <div className="w-2 h-2 rounded-full bg-primary"></div>
                          <p className="text-xs text-gray-500">Bezahlt</p>
                        </div>
                        <p className="text-lg font-semibold text-primary">
                          {umsatzDaten.aktuell.reduce((sum, d) => sum + d.bezahlt, 0).toLocaleString('de-DE', { maximumFractionDigits: 0 })} €
                        </p>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1.5 mb-1">
                          <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                          <p className="text-xs text-gray-500">Offen</p>
                        </div>
                        <p className="text-lg font-semibold text-amber-500">
                          {umsatzDaten.aktuell.reduce((sum, d) => sum + d.offen, 0).toLocaleString('de-DE', { maximumFractionDigits: 0 })} €
                        </p>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1.5 mb-1">
                          <div className="w-2 h-2 rounded-full bg-slate-400"></div>
                          <p className="text-xs text-gray-500">Vorjahr</p>
                        </div>
                        <p className="text-lg font-semibold text-slate-400">
                          {umsatzDaten.vorjahr.reduce((sum, d) => sum + d.bezahlt, 0).toLocaleString('de-DE', { maximumFractionDigits: 0 })} €
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </WidgetWrapper>
          </div>
          )}

          {/* Zeiterfassung Widget */}
          {isWidgetVisible('zeiterfassung') && (
          <div key="zeiterfassung">
            <WidgetWrapper title="Zeiterfassung" noScroll>
              <ZeiterfassungWidget />
            </WidgetWrapper>
          </div>
          )}

          {/* Aktive Projekte Widget */}
          {isWidgetVisible('projekte') && (
          <div key="projekte">
            <WidgetWrapper title="Aktive Projekte">
              {aktiveProjekte.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-500 text-sm">
                  <FolderOpen className="w-8 h-8 mb-2 text-gray-400" />
                  <p>Keine aktiven Projekte</p>
                  <Link
                    to="/projekte/neu"
                    className="mt-2 text-primary hover:underline text-xs"
                  >
                    Neues Projekt erstellen
                  </Link>
                </div>
              ) : (
                <div className="space-y-2">
                  {aktiveProjekte.map((projekt) => (
                    <Link
                      key={projekt.id}
                      to={`/projekte/${projekt.id}`}
                      className="block p-3 hover:bg-gray-50 rounded-lg transition border border-gray-100"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <FolderOpen className="w-3.5 h-3.5 text-blue-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{projekt.titel}</p>
                            <p className="text-xs text-gray-500 truncate">{projekt.kunde?.name || 'Kein Kunde'}</p>
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full ml-2 flex-shrink-0 ${
                          PROJEKT_STATUS_CONFIG[projekt.status]?.color || 'bg-gray-100 text-gray-700'
                        }`}>
                          {PROJEKT_STATUS_CONFIG[projekt.status]?.label || projekt.status}
                        </span>
                      </div>
                    </Link>
                  ))}
                  <Link
                    to="/projekte"
                    className="flex items-center justify-center gap-1 text-xs text-primary hover:underline pt-2"
                  >
                    Alle Projekte anzeigen
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              )}
            </WidgetWrapper>
          </div>
          )}

          {/* Top 10 Kunden */}
          {isWidgetVisible('top-kunden') && (
          <div key="top-kunden">
            <WidgetWrapper title="Top 10 Kunden nach Umsatz">
              {topKunden && topKunden.length > 0 ? (
                <TopKunden kunden={topKunden} />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  Keine Kundendaten vorhanden
                </div>
              )}
            </WidgetWrapper>
          </div>
          )}

          {/* Offene Anfragen - separates Widget */}
          {isWidgetVisible('offene-anfragen') && (
          <div key="offene-anfragen">
            <WidgetWrapper title="Offene Anfragen" headerAction={
              <Link to="/anfragen" className="text-xs text-primary hover:underline flex items-center gap-1">
                Alle anzeigen <ArrowRight className="w-3 h-3" />
              </Link>
            }>
              {recentAnfragen.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                  Keine offenen Anfragen
                </div>
              ) : (
                <div className="space-y-2">
                  {recentAnfragen.map((anfrage) => {
                    const displayBetreff = (anfrage.betreff || '').replace(/^🚨\s*NOTFALL\s*—\s*/i, '').trim() || '—';
                    return (
                      <AnfrageHoverPreview key={anfrage.id} anfrage={anfrage}>
                        <Link
                          to={`/anfragen/${anfrage.id}`}
                          className={`block p-3 hover:bg-gray-50 rounded-lg transition border ${
                            anfrage.istNotfall ? 'border-red-300 bg-red-50/40' : 'border-gray-100'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                {anfrage.istNotfall && (
                                  <span
                                    className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-red-600 text-white rounded shrink-0"
                                    title="Notdienst — Anruf wurde an den Notdienst weitergeleitet"
                                  >
                                    <AlertTriangle className="w-2.5 h-2.5" />
                                    Notdienst
                                  </span>
                                )}
                                <p className={`font-medium text-sm truncate ${anfrage.istNotfall ? 'text-red-900' : ''}`}>{displayBetreff}</p>
                              </div>
                              <p className="text-xs text-gray-500">{anfrage.kunde?.name || anfrage.extrahierteDaten?.name || 'Unbekannt'}</p>
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full ml-2 flex-shrink-0 ${
                              anfrage.status === 'neu' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {anfrage.status === 'neu' ? 'Neu' : 'In Bearb.'}
                            </span>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">{formatDate(anfrage.erstelltAm)}</p>
                        </Link>
                      </AnfrageHoverPreview>
                    );
                  })}
                </div>
              )}
            </WidgetWrapper>
          </div>
          )}

          {/* Anstehende Termine - separates Widget */}
          {isWidgetVisible('anstehende-termine') && (
          <div key="anstehende-termine">
            <WidgetWrapper title="Anstehende Termine">
              {upcomingTermine.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                  Keine Termine geplant
                </div>
              ) : (
                <div className="space-y-2">
                  {upcomingTermine.map((termin) => (
                    <Link
                      key={termin.id}
                      to={`/kalender?terminId=${termin.id}`}
                      className="block p-3 hover:bg-gray-50 rounded-lg transition border border-gray-100"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">
                            {termin.kunde ? termin.kunde.name : (termin.titel || 'Privat-Termin')}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatTerminDate(termin.datum)} um {termin.uhrzeit} Uhr
                          </p>
                        </div>
                        {isToday(termin.datum) && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 ml-2 flex-shrink-0">
                            Heute
                          </span>
                        )}
                        {isTomorrow(termin.datum) && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 ml-2 flex-shrink-0">
                            Morgen
                          </span>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </WidgetWrapper>
          </div>
          )}
        </ResponsiveGridLayout>

        {/* Quick Actions */}
        <div className="flex gap-4 mt-6">
          <Link
            to="/kunden"
            className="px-4 py-2 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition"
          >
            Alle Kunden anzeigen
          </Link>
          <Link
            to="/rechnungen"
            className="px-4 py-2 bg-surface border border-border text-text rounded-button font-medium hover:bg-gray-50 transition"
          >
            Alle Rechnungen
          </Link>
        </div>
      </div>
    </Layout>
  );
}

export default Dashboard;
